import React, { useContext, useEffect, useRef, useState } from 'react';
import { ModuleBase, ProgressCircular } from '@/components';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import Image from 'next/image';
import ConsumerAignalsDashboardImage from '@/assets/images/consumer-aignals-dashboard.png';
import YellowBeta from '@/assets/images/yellow_beta.png';
import { UserContext } from '@/components/ContextProviders';

const tableauScriptSrc = 'https://analytics.globaldata.com/javascripts/api/tableau.embedding.3.latest.min.js';
let tableauEmbeddingApiPromise: Promise<void> | null = null;

type DashboardParams = {
  token: string;
  dashboardUrl: string;
  userName: string;
};

type TableauIframeModuleModel = ModuleBaseModel<
  {
    site: string;
    workbook: string;
    view: string;
    username?: string;
  } & ModuleBasePropsModel
>;

const loadTableauEmbeddingApi = () => {
  if (customElements.get('tableau-viz')) {
    return Promise.resolve();
  }

  if (tableauEmbeddingApiPromise) {
    return tableauEmbeddingApiPromise;
  }

  tableauEmbeddingApiPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${tableauScriptSrc}"]`);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Tableau embedding API.')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.src = tableauScriptSrc;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load Tableau embedding API.')), {
      once: true,
    });
    document.head.appendChild(script);
  });

  return tableauEmbeddingApiPromise;
};

const TableauIframeModule = (data: TableauIframeModuleModel) => {
  const { workbook, view, username } = data.props;
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardParams | null>(null);
  const [embedError, setEmbedError] = useState<string>('');
  const tableauViewRef = useRef<HTMLDivElement | null>(null);
  const user = useContext(UserContext);

  useEffect(() => {

    if (workbook && view) {
      setLoading(true);
      setEmbedError('');

      const params = new URLSearchParams({
        task: 'getToken',
        workbook,
        view,
      });

      if (username === "Users Email" && user.email)
        params.set('username', user.email);


      fetch(`/api/tableau?${params.toString()}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Tableau token request failed with status ${response.status}`);
        }

        return response.json();
      }).then((data: DashboardParams) => {
        setDashboardData(data);
      }).catch((error) => {
        console.error('Error fetching Tableau embed URL:', error);
        setEmbedError('Failed to load Consumer Signals Dashboard. Please try again later.');
      }).finally(() => {
        setLoading(false);
      });


    } else {
      setDashboardData({
        token: '',
        dashboardUrl: '',
        userName: ''
      });
      setLoading(false);
      setEmbedError('Failed to load Consumer Signals Dashboard. Missing workbook or view parameter.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbook, view]);

  useEffect(() => {

    const container = tableauViewRef.current;
    let cancelled = false;
    let viz: (HTMLElement & {
      src?: string;
      token?: string;
      toolbar?: string;
      width?: string;
      height?: string;
    }) | null = null;

    if (!container || !dashboardData)
      return undefined;

    container.replaceChildren();

    const handleError = (event: Event) => {
      console.error('Tableau viz load error:', event);
      //const detail = (event as CustomEvent).detail;
      const message = 'Tableau viz failed to load.';
      setLoading(false);
      setEmbedError(message);
    };

    const handleInteractive = () => {
      setLoading(false);
    };

    loadTableauEmbeddingApi()
      .then(() => {
        if (cancelled)
          return;

        viz = document.createElement('tableau-viz') as HTMLElement & {
          src?: string;
          token?: string;
          toolbar?: string;
          width?: string;
          height?: string;
        };

        viz.src = dashboardData.dashboardUrl;
        viz.token = dashboardData.token;
        viz.toolbar = 'hidden';
        viz.width = '100%';
        viz.height = '960';
        viz.style.display = 'block';
        viz.style.width = '100%';

        const parameter = document.createElement('viz-parameter') as HTMLElement;
        parameter.setAttribute('name', ':tabs');
        parameter.setAttribute('value', 'no');
        viz.appendChild(parameter);

        viz.addEventListener('vizloaderror', handleError);
        viz.addEventListener('firstinteractive', handleInteractive);
        container.appendChild(viz);
      })
      .catch((error) => {
        console.error('Error loading Tableau embedding API:', error);
        setLoading(false);
        setEmbedError('Failed to load Consumer Signals Dashboard. Please try again later.');
      });

    return () => {
      cancelled = true;
      viz?.removeEventListener('vizloaderror', handleError);
      viz?.removeEventListener('firstinteractive', handleInteractive);
      container.replaceChildren();
    };

  }, [dashboardData]);

  return (
    <ModuleBase data={data}>
      <div className="flex w-full justify-center">
        <div className="container mx-auto px-4">
          <div className="flex w-full justify-center pb-8">
            <div className="flex w-full flex-row items-start md:w-[600px]">
              <Image
                src={ConsumerAignalsDashboardImage}
                alt="Consumer Signals Dashboard"
                className="mr-4 w-[52px] shrink-0"
                priority
              />
              <div className="flex flex-col">
                <div className="flex min-h-10 items-start text-2xl font-semibold text-[#f7c044] md:h-13 md:text-[34px]">
                  Consumer Signals Dashboard
                  <Image src={YellowBeta} alt="Beta" width={32} className="mt-2 ml-1" />
                </div>
                <div className="w-full text-base leading-5.5 text-[#1d1d1b]">
                  A monthly view of F&B consumer behaviour across social media,
                  <br className="hidden md:block" /> search, menus and retail listings. Click{' '}
                  <a
                    href="https://stylus.com/media/a0nazesb/consumersignalsdashboard_stylusnewtools_onepager.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                    title="Consumer Signals Dashboard Guide"
                  >
                    {' '}
                    here
                  </a>{' '}
                  to view a guide for this tool.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full justify-center">
        {!embedError && dashboardData?.token != "" && (
          <div ref={tableauViewRef} className="w-full" />
        )}
        {embedError && (
          <div className="flex h-56 items-center justify-center text-red-500">{embedError}</div>
        )}

        {loading && (
          <div className="loading-indicator absolute flex h-[280px] w-full items-center justify-center">
            <ProgressCircular indeterminate size={50} width={4} colorClassName="stroke-sky" />
          </div>
        )}
      </div>
    </ModuleBase>
  );
};

export default TableauIframeModule;
